import { Controller } from '@hotwired/stimulus'
import Cookies from 'js-cookie'

// Follow / unfollow toggle on a user profile.
//
// Renders as a single <button>; clicking sends POST or DELETE to the same
// follow endpoint based on the current state, with an optimistic UI swap
// and a revert if the request fails. The backend still returns redirects
// for plain HTML callers (button_to / no-JS); this controller forces a
// JSON Accept header so the controller's JSON branch returns a status code
// and the page doesn't navigate.
//
// For logged-out users the button still renders; clicking fires a
// guest_follow_intent PostHog event and redirects to /join.
//
// Values:
//   following:   Boolean   current state (also drives label + classes)
//   url:         String    the resource URL (POST creates, DELETE removes)
//   followeeId:  Number    the followee's numeric user ID (for PostHog)
//
// Targets:
//   label                the inner span whose text reads "Follow"/"Following"
//
// Classes:
//   follow               classes for the not-following visual state
//   following            classes for the following visual state
export default class extends Controller {
  static values = {
    following: Boolean,
    url: String,
    followeeId: Number,
  }
  static targets = ['label']
  static classes = ['follow', 'following']

  async toggle (event) {
    event.preventDefault()

    if (Cookies.get('user_signed_in') !== 'true') {
      this._redirectToJoinWithPendingFollow()
      return
    }

    if (this.requestInFlight) return
    this.requestInFlight = true

    const wasFollowing = this.followingValue
    const delta = wasFollowing ? -1 : 1
    this.followingValue = !wasFollowing
    this.applyState()
    this.updateFollowerCount(delta)

    try {
      const response = await fetch(this.urlValue, {
        method: wasFollowing ? 'DELETE' : 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    } catch (e) {
      this.followingValue = wasFollowing
      this.applyState()
      this.updateFollowerCount(-delta)
      console.error('Follow toggle failed:', e)
    } finally {
      this.requestInFlight = false
    }
  }

  _redirectToJoinWithPendingFollow() {
    // sendBeacon: this capture is immediately followed by navigation to /join,
    // so a batched/XHR request would be dropped when the page unloads before it flushes.
    window.posthog?.capture(
      'guest_follow_intent',
      { followee_id: this.followeeIdValue },
      { transport: 'sendBeacon' }
    )
    window.location.href = '/join?skip_profile=1&intent=' + encodeURIComponent(window.location.href)
  }

  updateFollowerCount (delta) {
    this.updateProfileFollowersCount(delta)
    this.updateOwnFollowingCount(delta)
  }

  // #followers-count is the profile page owner's follower total. Modal list
  // buttons follow other users — only bump the count when this button is for
  // that same profile user (the main Follow control).
  updateProfileFollowersCount (delta) {
    const el = document.getElementById('followers-count')
    if (!el) return

    const profileUserId = el.dataset.profileUserId
    if (profileUserId && String(this.followeeIdValue) !== String(profileUserId)) return

    this.applyCountDelta(el, delta, 'followers-tab-count')
  }

  // On your own profile, Follow/Following toggles in the modal change how many
  // people you follow — not your follower count. Also keep the modal tab label
  // in sync when you follow someone from the Followers list.
  updateOwnFollowingCount (delta) {
    const el = document.getElementById('following-count')
    if (!el) return

    const profileUserId = el.dataset.profileUserId
    if (!profileUserId || el.dataset.ownProfile !== 'true') return
    if (String(this.followeeIdValue) === String(profileUserId)) return

    this.applyCountDelta(el, delta, 'following-tab-count')
  }

  applyCountDelta (el, delta, tabElId) {
    const current = Number.isFinite(parseInt(el.dataset.count, 10))
      ? parseInt(el.dataset.count, 10)
      : (parseInt(el.textContent.replace(/[^\d-]/g, ''), 10) || 0)
    const next = Math.max(0, current + delta)
    el.dataset.count = String(next)
    el.textContent = String(next)

    const tabEl = document.getElementById(tabElId)
    if (!tabEl) return

    tabEl.dataset.count = String(next)
    tabEl.textContent = String(next)
  }

  applyState () {
    if (this.followingValue) {
      this.element.classList.remove(...this.followClasses)
      this.element.classList.add(...this.followingClasses)
      this.labelTarget.textContent = 'Following'
    } else {
      this.element.classList.remove(...this.followingClasses)
      this.element.classList.add(...this.followClasses)
      this.labelTarget.textContent = 'Follow'
    }
  }

  get csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
